import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ConfigProvider } from 'antd';
import { Toaster } from 'react-hot-toast';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { AuthPage } from './pages/AuthPage';
import { CompanyPage } from './pages/CompanyPage';
import DocumentsPage from './pages/client/DocumentsPage';
import { RegistrationWizard } from './components/registration/RegistrationWizard';
import { AdminLoginPage } from './admin/pages/AdminLoginPage';
import { ModerationPageNew as ModerationPage } from './admin/pages/ModerationPageNew';
import { ClientsPage } from './admin/pages/ClientsPage';
import { AdminContractsPage } from './admin/pages/AdminContractsPage';
import { CompanyContractsPage } from './admin/pages/CompanyContractsPage';
import { AdminRoute } from './admin/components/common/AdminRoute';
import { UserRole } from './admin/types/admin.types';
import { ContractGenerationPage } from './pages/ContractGenerationPage';
import ContractSigningPageNew from './pages/ContractSigningPage';
import { ClientLayout } from './components/client/ClientLayout';
import { ClientDashboard } from './pages/client/ClientDashboard';
import { OrdersPage } from './pages/client/OrdersPage';
import { OrderDetailPage } from './pages/client/OrderDetailPage';
import { CreateOrderPage } from './pages/client/CreateOrderPage';
import OrderSignaturePage from './pages/client/OrderSignaturePage';
import { ContractsPage } from './pages/client/ContractsPage';
import { ContractPage } from './pages/client/ContractPage';
import { ModerationPage as ClientModerationPage } from './pages/client/ModerationPage';
import { ProfilePage } from './pages/client/ProfilePage';
import { ProductsPage } from './pages/client/ProductsPage';
import { SetPasswordPage } from './pages/SetPasswordPage';
import { UsersManagementPage } from './admin/pages/UsersManagementPage';
import AdminProductsPage from './pages/admin/AdminProductsPage';
import AdminOrderSignatures from './pages/admin/AdminOrderSignatures';
import { DebugPage } from './pages/DebugPage';
import { ClientRoute } from './components/common/ClientRoute';
import { DocumentsPublicPage } from './pages/DocumentsPublicPage';

// Создаем клиент для React Query
const queryClient = new QueryClient({
    defaultOptions: {
        queries: {
            retry: 1,
            refetchOnWindowFocus: false,
        },
    },
});

// Компонент для защищенных маршрутов
const PrivateRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const { isAuthenticated, isLoading, user } = useAuth();

    console.log('PrivateRoute: isLoading =', isLoading, 'isAuthenticated =', isAuthenticated, 'user =', user);

    if (isLoading) {
        console.log('PrivateRoute: показываем загрузку');
        return (
            <div className="min-h-screen flex items-center justify-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
            </div>
        );
    }

    if (!isAuthenticated) {
        console.log('PrivateRoute: пользователь не авторизован, редирект на /auth');
        return <Navigate to="/auth" replace />;
    }

    console.log('PrivateRoute: пользователь авторизован, показываем контент');
    return <>{children}</>;
};

// Компонент для публичных маршрутов (только для неавторизованных)
const PublicRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const { isAuthenticated, isLoading, user } = useAuth();

    if (isLoading) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
            </div>
        );
    }

    // Если пользователь авторизован, определяем куда его редиректить в зависимости от роли
    if (isAuthenticated && user) {
        const userRole = user.role as UserRole;

        // Админы и модераторы идут в админ-панель
        if (userRole === UserRole.SUPER_ADMIN || userRole === UserRole.MODERATOR) {
            return <Navigate to="/admin/moderation" replace />;
        } else if (userRole === UserRole.SIGNATORY) {
            return <Navigate to="/admin/client-contracts" replace />;
        }

        // Клиенты идут в клиентскую часть
        return <Navigate to="/client/dashboard" replace />;
    }

    return <>{children}</>;
};

// Компонент для умного редиректа на главной странице
const HomeRedirect: React.FC = () => {
    const { isAuthenticated, isLoading, user } = useAuth();

    if (isLoading) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
            </div>
        );
    }

    if (!isAuthenticated || !user) {
        return <Navigate to="/auth" replace />;
    }

    const userRole = user.role as UserRole;

    // Редиректим в зависимости от роли
    if (userRole === UserRole.SUPER_ADMIN || userRole === UserRole.MODERATOR) {
        return <Navigate to="/admin/moderation" replace />;
    } else if (userRole === UserRole.SIGNATORY) {
        return <Navigate to="/admin/client-contracts" replace />;
    }

    // Клиенты и сотрудники
    return <Navigate to="/client/dashboard" replace />;
};

// Основные маршруты приложения
const AppRoutes: React.FC = () => {
    return (
        <Routes>
            {/* Публичные маршруты */}
            <Route
                path="/auth"
                element={
                    <PublicRoute>
                        <AuthPage />
                    </PublicRoute>
                }
            />

            {/* Страница документов - публичная */}
            <Route path="/docs" element={<DocumentsPublicPage />} />

            {/* Редиректы со старых маршрутов на новые (только для клиентов) */}
            <Route path="/dashboard" element={<ClientRoute><Navigate to="/client/dashboard" replace /></ClientRoute>} />
            <Route path="/company" element={<ClientRoute><Navigate to="/client/company" replace /></ClientRoute>} />
            <Route path="/documents" element={<ClientRoute><Navigate to="/client/documents" replace /></ClientRoute>} />

            {/* Регистрация остается отдельно - без авторизации */}
            <Route
                path="/registration"
                element={<RegistrationWizard />}
            />

            {/* Новые клиентские маршруты */}
            <Route
                path="/client"
                element={
                    <ClientRoute>
                        <ClientLayout />
                    </ClientRoute>
                }
            >
                <Route index element={<Navigate to="/client/dashboard" replace />} />
                <Route path="dashboard" element={<ClientDashboard />} />
                <Route path="products" element={<ProductsPage />} />
                <Route path="orders" element={<OrdersPage />} />
                <Route path="orders/create" element={<CreateOrderPage />} />
                <Route path="orders/:orderId" element={<OrderDetailPage />} />
                <Route path="order-signatures/:orderSignatureId" element={<OrderSignaturePage />} />
                <Route path="contracts" element={<ContractsPage />} />
                <Route path="contract" element={<ContractPage />} />
                <Route path="contract-generation" element={<ContractGenerationPage />} />
                <Route path="company" element={<CompanyPage />} />
                <Route path="profile" element={<ProfilePage />} />
                <Route path="debug" element={<DebugPage />} />
                <Route path="moderation" element={<ClientModerationPage />} />
                <Route path="documents" element={<DocumentsPage />} />
            </Route>

            {/* Маршруты для подписания договоров */}
            <Route
                path="/contract-signing/:contractId"
                element={
                    <ClientRoute>
                        <ContractSigningPageNew />
                    </ClientRoute>
                }
            />
            <Route
                path="/contracts"
                element={
                    <ClientRoute>
                        <ContractGenerationPage />
                    </ClientRoute>
                }
            />

            {/* Вход в админ-панель */}
            <Route path="/admin" element={<AdminLoginPage />} />

            {/* Публичный маршрут для установки пароля по токену */}
            <Route path="/set-password/:token" element={<SetPasswordPage />} />

            {/* Админ-панель */}
            <Route
                path="/admin/moderation"
                element={
                    <AdminRoute requiredRoles={[UserRole.MODERATOR, UserRole.SUPER_ADMIN]}>
                        <ModerationPage />
                    </AdminRoute>
                }
            />

            <Route
                path="/admin/contracts"
                element={
                    <AdminRoute requiredRoles={[UserRole.SIGNATORY, UserRole.SUPER_ADMIN]}>
                        <div>Управление договорами (в разработке)</div>
                    </AdminRoute>
                }
            />

            <Route
                path="/admin/client-contracts"
                element={
                    <AdminRoute requiredRoles={[UserRole.MODERATOR, UserRole.SUPER_ADMIN]}>
                        <AdminContractsPage />
                    </AdminRoute>
                }
            />

            <Route
                path="/admin/company-contracts/:companyId"
                element={
                    <AdminRoute requiredRoles={[UserRole.MODERATOR, UserRole.SUPER_ADMIN]}>
                        <CompanyContractsPage />
                    </AdminRoute>
                }
            />

            <Route
                path="/admin/users"
                element={
                    <AdminRoute requiredRoles={[UserRole.SUPER_ADMIN]}>
                        <UsersManagementPage />
                    </AdminRoute>
                }
            />

            <Route
                path="/admin/clients"
                element={
                    <AdminRoute requiredRoles={[UserRole.MODERATOR, UserRole.SUPER_ADMIN]}>
                        <ClientsPage />
                    </AdminRoute>
                }
            />

            <Route
                path="/admin/products"
                element={
                    <AdminRoute requiredRoles={[UserRole.MODERATOR, UserRole.SUPER_ADMIN]}>
                        <AdminProductsPage />
                    </AdminRoute>
                }
            />

            <Route
                path="/admin/order-signatures"
                element={
                    <AdminRoute requiredRoles={[UserRole.MODERATOR, UserRole.SUPER_ADMIN]}>
                        <AdminOrderSignatures />
                    </AdminRoute>
                }
            />

            <Route
                path="/admin/reports"
                element={
                    <AdminRoute>
                        <div>Отчеты и аналитика (в разработке)</div>
                    </AdminRoute>
                }
            />

            {/* Редирект по умолчанию - умный редирект в зависимости от роли */}
            <Route path="/" element={<HomeRedirect />} />

            {/* 404 страница */}
            <Route
                path="*"
                element={
                    <div className="min-h-screen flex items-center justify-center">
                        <div className="text-center">
                            <h1 className="text-4xl font-bold text-gray-900 mb-4">404</h1>
                            <p className="text-gray-600 mb-4">Страница не найдена</p>
                            <button
                                onClick={() => window.history.back()}
                                className="btn-primary"
                            >
                                Вернуться назад
                            </button>
                        </div>
                    </div>
                }
            />
        </Routes>
    );
};

// Главный компонент приложения
const App: React.FC = () => {
    return (
        <ConfigProvider
            theme={{
                token: {
                    colorPrimary: '#2563eb',
                },
            }}
        >
        <QueryClientProvider client={queryClient}>
            <Router>
                <AuthProvider>
                    <div className="App">
                        <Toaster
                            position="top-right"
                            reverseOrder={false}
                            toastOptions={{
                                duration: 4000,
                                style: {
                                    background: '#fff',
                                    color: '#363636',
                                    padding: '16px',
                                    borderRadius: '8px',
                                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                                },
                                success: {
                                    duration: 3000,
                                    iconTheme: {
                                        primary: '#10b981',
                                        secondary: '#fff',
                                    },
                                },
                                error: {
                                    duration: 4000,
                                    iconTheme: {
                                        primary: '#ef4444',
                                        secondary: '#fff',
                                    },
                                },
                            }}
                        />
                        <AppRoutes />
                    </div>
                </AuthProvider>
            </Router>
        </QueryClientProvider>
        </ConfigProvider>
    );
};

export default App;
